import React from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  HeroTextTwoColumnSection as HeroTextTwoColumnSectionType,
  HTSVariants,
} from '../../types/heroTextSection'
import HeroTextSectionColumn from './HeroTextSectionColumn'
import getColor, { getTextColor } from '../../utils/colors'
import useBrand from '../../hooks/useBrand'
import formatContentfulClasses, {
  getModuleClasses,
} from '../../utils/customClasses'
import { MatchClasses } from '../../types/contentfulAttributes'
import useMatchData from '../../hooks/useMatchData'
import * as successStoryClasses from '../../../topresume.com/src/styles/success-stories.module.css'
import { withHideOn } from '../../hocs/withHideOn'
import HeroTextTwoColumnCenteredSection from './HeroTextTwoColumnCenteredSection'

export interface HeroTextTwoColumnSectionProps {
  row: HeroTextTwoColumnSectionType
  sectionClass?: string
}

const HeroTextTwoColumnSection: React.FC<HeroTextTwoColumnSectionProps> = ({
  row,
  sectionClass,
}) => {
  const { classes, cx } = useStyles(row)
  const brand = useBrand()
  const { isError, isLoading, match } = useMatchData()

  const isTmReviewSection = row.codeClasses?.includes(
    MatchClasses.ResumeReviewSection
  )

  // Don't load the match resume/CV review section if
  // 1. there's an error
  // 2. there's is no match after the API finishes loading
  if (isTmReviewSection && (isError || (!isLoading && !match))) {
    return null
  }

  switch (row.variant) {
    case HTSVariants.ColumnCenteredBackground:
      return (
        <HeroTextTwoColumnCenteredSection
          sectionClass={sectionClass}
          row={row}
        />
      )
    default:
      return (
        <HeroTextSectionColumn
          displayClass={cx(
            {
              [classes.background]: true,
              [classes.columnsTR]:
                brand.name !== 'ZipJob' &&
                row.variant !== HTSVariants.ReverseColumn &&
                row.variant !== HTSVariants.ReverseColumnWideImage &&
                row.variant !== HTSVariants.ReverseColumnHero,
              [classes.columnsReverseTR]:
                brand.name !== 'ZipJob' &&
                (row.variant === HTSVariants.ReverseColumn ||
                  row.variant === HTSVariants.ReverseColumnWideImage ||
                  row.variant === HTSVariants.ReverseColumnHero),
              [classes.columns]:
                brand.name === 'ZipJob' &&
                row.variant !== HTSVariants.ReverseColumn &&
                row.variant !== HTSVariants.ReverseColumnWideImage &&
                row.variant !== HTSVariants.ReverseColumnHero,
              [classes.columnsReverse]:
                brand.name === 'ZipJob' &&
                (row.variant === HTSVariants.ReverseColumn ||
                  row.variant === HTSVariants.ReverseColumnWideImage ||
                  row.variant === HTSVariants.ReverseColumnHero),
              [classes.columnsContact]:
                row.variant === HTSVariants.ContactColumn,
            },
            ...formatContentfulClasses(row.codeClasses),
            ...getModuleClasses(row.codeClasses, successStoryClasses)
          )}
          sectionClass={cx(sectionClass)}
          titleClass={cx({
            [classes.subtitle]: true,
            [classes.subtitleCurrentColor]: brand.name !== 'ZipJob',
            [classes.subtitleTR]: brand.name !== 'ZipJob',
            [classes.centered]: row.codeClasses?.includes('ss-3-packages'),
          })}
          row={row}
        />
      )
  }
}

const useStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => {
    const bgColor = getColor(backgroundColor, theme)
    const textColor = getTextColor(backgroundColor, theme)

    return {
      background: {
        backgroundColor: bgColor,
        color: textColor,
      },
      columns: {
        flexDirection: 'row',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          justifyContent: 'center',
        },
      },
      columnsReverse: {
        flexDirection: 'row-reverse',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          justifyContent: 'center',
        },
      },
      columnsTR: {
        flexDirection: 'row',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column-reverse',
          justifyContent: 'center',
        },
      },
      columnsReverseTR: {
        flexDirection: 'row-reverse',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column-reverse',
          justifyContent: 'center',
        },
      },
      columnsContact: {
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
        },
      },
      subtitle: {
        color: theme.palette.secondary.dark,
        textTransform: 'uppercase',
        fontSize: '.8rem',
        letterSpacing: '.9px',
        fontWeight: 'bold',
        marginBottom: '.7rem',
        [theme.breakpoints.down('md')]: {
          marginBottom: '.7rem',
        },
      },
      subtitleCurrentColor: {
        color: 'currentColor',
      },
      subtitleTR: {
        fontSize: '.88rem',
        letterSpacing: '.09rem',
        margin: '0',
      },
      centered: {
        textAlign: 'center',
      },
    }
  }
)

export default withHideOn(HeroTextTwoColumnSection)
